import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BackButton } from '../components/NavButtons';
import { ClosurePanel } from '../components/seller/ClosurePanel';
import { usePlatformSettings } from '../hooks/usePlatformSettings';
import axios from 'axios';
import {
  ArrowLeft, CalendarBlank, Lightning, Prohibit, Check, CaretLeft, CaretRight,
  Sun, Calendar, CalendarDots
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AvailabilityManager = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [listing, setListing] = useState(null);
  const [blockedDates, setBlockedDates] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [successMsg, setSuccessMsg] = useState('');
  const platformSettings = usePlatformSettings();

  const fetchData = useCallback(async () => {
    try {
      const today = new Date();
      const futureDate = new Date(today);
      futureDate.setDate(futureDate.getDate() + 90);
      const fromStr = today.toISOString().split('T')[0];
      const toStr = futureDate.toISOString().split('T')[0];

      const [listingRes, availRes] = await Promise.all([
        axios.get(`${API_URL}/api/listings/${listingId}`),
        axios.get(`${API_URL}/api/availability/${listingId}?from_date=${fromStr}&to_date=${toStr}`)
      ]);
      setListing(listingRes.data);
      setBlockedDates(new Set(availRes.data.blocked_dates || []));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [listingId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const toggleDate = async (dateStr) => {
    const isBlocked = blockedDates.has(dateStr);
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/availability/${listingId}`, {
        dates: [dateStr],
        available: isBlocked
      });

      setBlockedDates(prev => {
        const next = new Set(prev);
        if (isBlocked) next.delete(dateStr);
        else next.add(dateStr);
        return next;
      });
      showSuccess(isBlocked ? `${dateStr} unblocked` : `${dateStr} blocked`);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleQuickAction = async (period, available) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/api/availability/${listingId}/quick-set`, {
        period, available
      });

      // Refresh data
      await fetchData();
      showSuccess(res.data.message);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };
  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().split('T')[0];

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Day headers
    for (let d = 0; d < 7; d++) {
      days.push(
        <div key={`h-${d}`} className="text-center text-xs font-semibold text-slate-400 py-2">
          {dayLabels[d]}
        </div>
      );
    }

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`e-${i}`} className="h-12" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isPast = dateObj < today;
      const isToday = dateStr === todayStr;
      const isBlocked = blockedDates.has(dateStr);

      days.push(
        <button
          key={dateStr}
          disabled={isPast || saving}
          onClick={() => !isPast && toggleDate(dateStr)}
          data-testid={`date-${dateStr}`}
          className={`h-12 rounded-xl text-sm font-medium transition-all relative
            ${isPast ? 'text-slate-300 cursor-not-allowed bg-slate-50' :
              isBlocked ? 'bg-red-100 text-red-700 border-2 border-red-300 hover:bg-red-200' :
              'bg-emerald-50 text-emerald-700 border-2 border-emerald-200 hover:bg-emerald-100'}
            ${isToday ? 'ring-2 ring-blue-400 ring-offset-1' : ''}
          `}
        >
          {day}
          {!isPast && (
            <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[9px] font-bold">
              {isBlocked ? <Prohibit size={10} weight="bold" /> : <Check size={10} weight="bold" />}
            </span>
          )}
        </button>
      );
    }

    return days;
  };

  // Count stats
  const blockedCount = blockedDates.size;
  const totalUpcoming = 30;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="availability-manager">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-3">
          <BackButton />
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900 truncate">Availability Manager</h1>
            <p className="text-xs text-slate-500 truncate">{listing?.title}</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Success Message */}
        {successMsg && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-emerald-700 text-sm font-medium flex items-center gap-2 animate-in slide-in-from-top" data-testid="success-msg">
            <Check size={18} weight="bold" /> {successMsg}
          </div>
        )}

        {/* Quick Actions */}
        <Card className="border-slate-200 overflow-hidden">
          <CardHeader className="pb-3 bg-gradient-to-r from-slate-800 to-slate-700">
            <CardTitle className="text-base font-semibold text-white flex items-center gap-2">
              <Lightning size={20} weight="fill" /> Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            <p className="text-sm text-slate-500 mb-4">Quickly block or unblock your space for upcoming periods</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Tomorrow */}
              <div className="border-2 border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Sun size={20} className="text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">Tomorrow</p>
                    <p className="text-xs text-slate-400">Next day</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => handleQuickAction('day', false)}
                    disabled={saving}
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs"
                    data-testid="block-day-btn">
                    <Prohibit size={14} className="mr-1" /> Block
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => handleQuickAction('day', true)}
                    disabled={saving}
                    className="flex-1 border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs"
                    data-testid="unblock-day-btn">
                    <Check size={14} className="mr-1" /> Open
                  </Button>
                </div>
              </div>

              {/* Next Week */}
              <div className="border-2 border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">Next Week</p>
                    <p className="text-xs text-slate-400">7 days</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => handleQuickAction('week', false)}
                    disabled={saving}
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs"
                    data-testid="block-week-btn">
                    <Prohibit size={14} className="mr-1" /> Block
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => handleQuickAction('week', true)}
                    disabled={saving}
                    className="flex-1 border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs"
                    data-testid="unblock-week-btn">
                    <Check size={14} className="mr-1" /> Open
                  </Button>
                </div>
              </div>

              {/* Next Month */}
              <div className="border-2 border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
                    <CalendarDots size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">Next Month</p>
                    <p className="text-xs text-slate-400">30 days</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline"
                    onClick={() => handleQuickAction('month', false)}
                    disabled={saving}
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-xs"
                    data-testid="block-month-btn">
                    <Prohibit size={14} className="mr-1" /> Block
                  </Button>
                  <Button size="sm" variant="outline"
                    onClick={() => handleQuickAction('month', true)}
                    disabled={saving}
                    className="flex-1 border-emerald-200 text-emerald-600 hover:bg-emerald-50 rounded-lg text-xs"
                    data-testid="unblock-month-btn">
                    <Check size={14} className="mr-1" /> Open
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calendar View */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                <CalendarBlank size={20} /> Date Calendar
              </CardTitle>
              <div className="flex items-center gap-1">
                <button onClick={prevMonth} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="prev-month-btn">
                  <CaretLeft size={18} className="text-slate-600" />
                </button>
                <span className="text-sm font-semibold text-slate-700 min-w-[140px] text-center" data-testid="current-month">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </span>
                <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="next-month-btn">
                  <CaretRight size={18} className="text-slate-600" />
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Legend */}
            <div className="flex items-center gap-4 mb-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded bg-emerald-100 border-2 border-emerald-200" />
                <span className="text-slate-600">Available</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded bg-red-100 border-2 border-red-300" />
                <span className="text-slate-600">Blocked</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded bg-slate-50 border border-slate-200" />
                <span className="text-slate-600">Past</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 rounded border-2 border-slate-200 ring-2 ring-blue-400 ring-offset-1" />
                <span className="text-slate-600">Today</span>
              </div>
            </div>

            <p className="text-xs text-slate-400 mb-3">Click any future date to toggle availability</p>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1.5" data-testid="calendar-grid">
              {renderCalendar()}
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-red-600" data-testid="blocked-count">{blockedCount}</p>
              <p className="text-xs text-slate-500 mt-1">Blocked Dates</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600" data-testid="available-status">
                {blockedCount === 0 ? 'Open' : 'Partial'}
              </p>
              <p className="text-xs text-slate-500 mt-1">Status</p>
            </CardContent>
          </Card>
        </div>

        {/* Timed closures (maintenance, lunch, holiday …) */}
        {platformSettings.seller_closures !== false && (
          <ClosurePanel listingId={listingId} />
        )}
      </div>
    </div>
  );
};

export default AvailabilityManager;
